<template>
  <label
    class="FormChecklistItem"
    data-testid="checklistItem"
  >
    <div class="FormChecklistItem__container">
      <input
        v-model="modelValue"
        class="FormChecklistItem__input"
        v-bind="{name, value, type}"
        @input="haptic()"
      >

      <div class="FormChecklistItem__iconContainer">
        <Transition>
          <Icon
            v-if="Array.isArray(modelValue) ? modelValue.includes(value) : modelValue === value"
            class="FormChecklistItem__icon"
            name="check"
          />
        </Transition>
      </div>
    </div>
    <div class="FormChecklistItem__label">
      {{ label }}
    </div>
  </label>
</template>

<script lang="ts" generic="T" setup>
import Icon from "@/components/Icon.vue";
import haptic from "@/lib/utils/platform/haptic";

const modelValue = defineModel<T | Array<T>>({
  required: true,
});

const {
  type = "checkbox",
} = defineProps<{
  name: string,
  label: string,
  value: T,
  type?: "checkbox" | "radio",
}>();
</script>

<style lang="scss" scoped>
.FormChecklistItem {
  position: relative;

  display: grid;
  grid: auto / min-content auto;
  align-items: center;
  height: var(--inputInnerHeight);

  &:not(:has(:disabled)) {
    cursor: pointer;
  }

  & + &::before {
    position: absolute;
    left: 0;
    bottom: 100%;

    width: 100%;

    border-top: 1px solid var(--themeColorNeutral900);

    content: "";
  }

  & + &:has(:disabled)::before {
    border-top-color: var(--themeColorNeutral400);
  }
}

.FormChecklistItem__container {
  position: relative;

  display: grid;
  place-items: center;
  height: 100%;
  aspect-ratio: 1;

  background-color: var(--themeColorNeutral150);

  transition: 350ms background-color;

  &:has(:checked) {
    background-color: var(--themeColorSecondary);
  }

  &:has(:disabled) {
    background-color: var(--themeColorNeutral150);
  }

  .FormChecklistItem:first-of-type & {
    border-radius: calc(var(--borderRadius) - var(--outlineWidth)) 0 0;
  }

  .FormChecklistItem:last-of-type & {
    border-radius: 0 0 0 calc(var(--borderRadius) - var(--outlineWidth));
  }
}

.FormChecklistItem__input {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;
  appearance: none;
}

.FormChecklistItem__iconContainer {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;

  border-radius: 4px;
  outline: 0px solid var(--themeDarkTransparent200);
  background-color: var(--themeColorInputBackground);

  transition: 350ms outline;

  input:disabled ~ & {
    background-color: var(--themeColorNeutral0);
  }

  .FormChecklistItem:has(:focus-visible) & {
    outline: 3px solid var(--themeDarkTransparent200);
  }
}

.FormChecklistItem__icon {
  width: 16px;
  height: 16px;

  &.v-enter-active {
    transition: 350ms var(--easeOutBack);
    transition-property: opacity, transform;
  }

  &.v-leave-active {
    transition: 175ms var(--easeInQuad);
    transition-property: opacity, transform;
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }
}

.FormChecklistItem__label {
  padding: var(--fontSafariOffset) 12px 0;
  overflow: hidden;

  font: var(--fontWeightNormal) 18px / 1 var(--fontFamilyMono);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
